import React, { useRef, useEffect, useState } from 'react';
import { TypeAnimation } from 'react-type-animation';
import './CodeBackground.css';

const codeSequenceText = `
  // Carles Falcó Riera - Senior Full Stack Engineer
  // Accessing portfolio module...

  import { Coffee } from '@daily-routine';
  import { Skills } from '@carles-falco/core';

  // -- Core Principles & Practices --

  /**
   * @function buildRobustSoftware
   * @description Prioritizing Clean Code & SOLID principles.
   */
  const buildRobustSoftware = async (module: string): Promise<boolean> => {
    console.log(\`Building \${module} with TDD...\`);
    // TODO: Drink more coffee
    return true;
  };

  // Encouraging collaborative development and knowledge sharing
  const promoteTeamwork = (team: string[]): void => {
    team.forEach(member => {
      console.log(\`Collaborating with \${member} on code reviews.\`);
    });
  };

  // -- Current Focus: Cloud Modernization & DevSecOps --

  interface CloudMigrationStrategy {
    legacySystem: string;
    targetCloud: 'GCP' | 'AWS' | 'Azure';
  }

  const planCloudModernization = (strategy: CloudMigrationStrategy): void => {
    console.log(\`Starting migration from \${strategy.legacySystem} to \${strategy.targetCloud}...\`);
    // Performing white-box security audits...
  };

  // Execute main operations
  (async () => {
    await buildRobustSoftware("UserManagementService");
    promoteTeamwork(["Alice", "Bob", "Charlie"]);
    planCloudModernization({
      legacySystem: "ColdFusion Monolith",
      targetCloud: "GCP",
    });
    console.log("// P.S. Hire me? :)");
  })();
`;

interface CodeBackgroundProps {
  isFocused: boolean;
  setIsFocused: (isFocused: boolean) => void;
  isMinimized: boolean;
  setIsMinimized: (isMinimized: boolean) => void;
}

const CodeBackground = ({
  isFocused,
  setIsFocused,
  isMinimized,
  setIsMinimized
}: CodeBackgroundProps) => {

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isTyping, setIsTyping] = useState(true);

  const modalRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const offsetRef = useRef({ x: 0, y: 0 });
  const isPositionInitialized = useRef(false); 

  useEffect(() => {
    if (isTyping && !isMinimized) {
      const interval = setInterval(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [isTyping, isMinimized]);

  const handleUserScroll = () => {
    if (isTyping && scrollContainerRef.current) {
      const el = scrollContainerRef.current;
      if (el.scrollTop < el.scrollHeight - el.clientHeight - 50) {
        setIsTyping(false);
      }
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      requestAnimationFrame(() => {
        setPosition({
          x: e.clientX - offsetRef.current.x,
          y: e.clientY - offsetRef.current.y,
        });
      });
    };
    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
      }
    };
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp, { once: true });
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleDragStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDragging(true);
    offsetRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleModalOpen = () => {
    if (isFocused) return; 
    setIsFocused(true);
    setIsMinimized(false);

    if (!isPositionInitialized.current) {
        setTimeout(() => {
            if (modalRef.current) {
                const rect = modalRef.current.getBoundingClientRect();
                const initialX = (window.innerWidth / 2) - (rect.width / 2);
                const initialY = (window.innerHeight / 2) - (rect.height / 2);
                setPosition({ x: initialX, y: initialY });
                isPositionInitialized.current = true;
            }
        }, 0); 
    }
  };

  const handleClose = () => {
    setIsFocused(false);
    setIsMinimized(false);
  };

  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const getModalStyle = (): React.CSSProperties => {
    if (!isFocused || isMinimized) return {};
    return {
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'none'
    };
};


  return (
    <div
      ref={modalRef}
      className={`
        code-background-container
        ${isFocused ? 'focused' : ''}
        ${isMinimized ? 'minimized' : ''}
        ${isDragging ? 'dragging' : ''}
      `}
      onClick={handleModalOpen}
      style={getModalStyle()} 
    >

      {isFocused && (
        <div
          className="code-modal-header"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={handleDragStart}
        >
          <div className="code-modal-controls">
            <span className="control-btn btn-red" onClick={handleClose}>
              <span className="btn-icon">×</span>
            </span>
            <span className="control-btn btn-yellow" onClick={handleMinimize}>
              <span className="btn-icon">−</span>
            </span>
          </div>
          <span className="code-modal-title">CarlesFalcó_Portfolio.tsx</span>
        </div>
      )}

      <div
        className="code-scroll-wrapper"
        ref={scrollContainerRef}
        onScroll={handleUserScroll}
      >
        <TypeAnimation
          sequence={[
            'Connecting to server...', 1000,
            'Compiling secure module...', 1000,
            'Access granted. Loading code...', 1000,
            '', 2200,

            () => {
              if (modalRef.current) { 
                const el = modalRef.current.querySelector('.typing-code-animation');
                if (el) {
                  el.classList.remove('code-loading');
                  el.classList.add('code-loaded');
                }
              }
            },
            1000,

            codeSequenceText,
            1000,

            () => { setIsTyping(false); }
          ]}
          wrapper="div"
          speed={70}
          cursor={false}
          className="typing-code-animation code-loading"
        />
      </div>
    </div>
  );
};

export default CodeBackground;